import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const images = [
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/7b8ca49b50f5c7f8666e0caddb405160.jpeg',
    alt: 'Balancín venezolano — industria petrolera nacional',
    span: 'row-span-2',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/c25740b4ca3d79a40f8de4e3f79abe6e.webp',
    alt: 'Plataforma de perforación costa afuera',
    span: '',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/121ddf50a8ac50f36e3dd96fc8f3dfb3.jpeg',
    alt: 'Laboratorio de formulación de aditivos',
    span: '',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/2657df7d0552c5fecf431fcecbd2d1c4.jpeg',
    alt: 'Distribución de productos químicos a granel',
    span: '',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/82c727ebc95c127b734df257ae7b9094.jpeg',
    alt: 'Pruebas de evaluación en baño caliente',
    span: '',
  },
];

const pillars = [
  { icon: 'ri-flask-line', label: 'I+D Propio', desc: 'Laboratorio interno de formulación' },
  { icon: 'ri-shield-check-line', label: 'Calidad Certificada', desc: 'Estándares internacionales' },
  { icon: 'ri-global-line', label: 'Alcance Internacional', desc: 'Exportación a toda la región' },
  { icon: 'ri-time-line', label: '+15 Años', desc: 'De experiencia en campo' },
];

export default function HomeAbout() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full py-20 md:py-28 bg-background-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* ── Top label ── */}
        <div className={`flex items-center gap-3 mb-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="w-8 h-px bg-primary-500" />
          <span className="text-primary-600 text-xs font-semibold tracking-widest uppercase">Quiénes Somos</span>
        </div>

        {/* ── Main grid: text left + images right ── */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">

          {/* ── Left: text block ── */}
          <div className="flex-1 max-w-xl">
            <h2
              className={`font-heading font-bold text-3xl md:text-4xl text-foreground-950 leading-tight mb-6 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            >
              Soluciones químicas para la{' '}
              <span className="text-primary-500">industria petrolera</span> venezolana y global
            </h2>

            <p
              className={`text-foreground-600 text-base leading-relaxed mb-4 transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            >
              <strong className="text-foreground-800">Poly Alphas Chemicals C.A.</strong> es una empresa venezolana
              especializada en el desarrollo, fabricación y suministro de aditivos y fluidos químicos para
              operaciones de perforación, producción y completación de pozos.
            </p>

            <p
              className={`text-foreground-500 text-sm leading-relaxed mb-8 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            >
              Contamos con un laboratorio propio de I+D donde formulamos y evaluamos cada producto
              antes de llevarlo al campo. Nuestro portafolio abarca desde inhibidores de corrosión
              hasta emulsificantes, biocidas y productos especiales adaptados a las condiciones
              más exigentes de los yacimientos venezolanos.
            </p>

            {/* Pillars grid */}
            <div
              className={`grid grid-cols-2 gap-4 mb-8 transition-all duration-700 delay-250 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            >
              {pillars.map((p) => (
                <div key={p.label} className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className={`${p.icon} text-primary-600 text-base`} />
                  </div>
                  <div>
                    <p className="text-foreground-900 text-sm font-semibold leading-tight">{p.label}</p>
                    <p className="text-foreground-500 text-xs mt-0.5">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div
              className={`flex flex-wrap gap-3 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            >
              <Link
                to="/nosotros"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line" />
                Conoce más sobre nosotros
              </Link>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 hover:border-primary-400 text-foreground-700 hover:text-primary-600 text-sm font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line" />
                Contáctanos
              </Link>
            </div>
          </div>

          {/* ── Right: image mosaic ── */}
          <div
            className={`flex-shrink-0 w-full lg:w-[520px] transition-all duration-800 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
          >
            <div className="grid grid-cols-2 grid-rows-3 gap-3 h-[480px] md:h-[560px]">

              {/* Image 1 — tall left */}
              <div className="row-span-2 relative rounded-2xl overflow-hidden group">
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/7b8ca49b50f5c7f8666e0caddb405160.jpeg"
                  alt="Balancín venezolano — industria petrolera nacional"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white text-xs font-medium bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
                  Industria Nacional
                </span>
              </div>

              {/* Image 2 — top right */}
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/c25740b4ca3d79a40f8de4e3f79abe6e.webp"
                  alt="Plataforma de perforación costa afuera"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white text-xs font-medium bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
                  Offshore
                </span>
              </div>

              {/* Image 3 — middle right */}
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/121ddf50a8ac50f36e3dd96fc8f3dfb3.jpeg"
                  alt="Laboratorio de formulación"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white text-xs font-medium bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
                  Laboratorio I+D
                </span>
              </div>

              {/* Image 4 — bottom left */}
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/2657df7d0552c5fecf431fcecbd2d1c4.jpeg"
                  alt="Distribución a granel"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white text-xs font-medium bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
                  Logística
                </span>
              </div>

              {/* Image 5 — bottom right */}
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/82c727ebc95c127b734df257ae7b9094.jpeg"
                  alt="Pruebas de evaluación"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white text-xs font-medium bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
                  Evaluación
                </span>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}